'use client';

import type {
  Breadcrumb,
  ProductDetailData,
} from '@eliadmin/core/src/api/products/types/product-types';
import type { BreadcrumbsProps } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import React, { useState, type PropsWithChildren, useRef, useEffect } from 'react';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export type PageHeaderProps = {
  heading?: string;
  /** The route owns the primary H1 by default; reusable/secondary headers must opt out. */
  headingRole?: 'primary' | 'section' | 'presentation';
  description?: string;
  // should be Link component from next/link
  headerLink?: React.ReactNode;
  mobileAction?: React.ReactNode;

  variant?: 'default' | 'center';

  className?: {
    wrapper?: string;
    container?: string;
    content?: string;
    header?: string;
    description?: string;
    breadcrumbs?: BreadcrumbsProps['className'];
    heading_row?: string;
  };
  breadcrumb?: Breadcrumb;
  product?: ProductDetailData;
};

export function PageHeader({ variant, ...props }: PropsWithChildren<PageHeaderProps>) {
  const { t } = useTranslations();
  // TODO: handle if the description is already short
  const [isExtended, setShowMore] = useState(false);

  const [showToggle, setShowToggle] = useState(false);

  const descriptionRef = useRef<HTMLDivElement>(null);

  const templateSettings = useGlobalStore(s => s.templateSettings);
  const HeadingTag =
    props.headingRole === 'section' ? 'h2' : props.headingRole === 'presentation' ? 'div' : 'h1';

  useEffect(() => {
    if (templateSettings.category?.category_description_always_open?.enabled) {
      setShowMore(true);
      setShowToggle(false);
      return;
    }

    const checkDescriptionLines = () => {
      const descriptionElement = descriptionRef.current;
      if (descriptionElement) {
        // Get computed style of the element
        const style = window.getComputedStyle(descriptionElement);
        // Extract line height, fallback to a default value if it's 'normal' or not set
        const lineHeight = parseInt(style.lineHeight, 10) || parseInt(style.fontSize, 10) * 1.2;
        // Calculate the number of lines in the description
        const lines = descriptionElement.scrollHeight / lineHeight;
        // Check if the number of lines is greater than 6
        setShowToggle(lines > 6);
      }
    };

    checkDescriptionLines();
    // Optionally, add resize event listener if dynamic content or window resizing is expected
    window.addEventListener('resize', checkDescriptionLines);
    return () => window.removeEventListener('resize', checkDescriptionLines);
  }, [props.description]);

  return (
    <div
      className={cn(
        'flex w-full flex-col rounded-none bg-background py-0 sm:rounded-lg sm:pt-4',
        (props.breadcrumb && props.heading) ||
          (props.breadcrumb && props.description) ||
          (props.breadcrumb && props.children)
          ? 'gap-4'
          : '',
        'ea-homepagepage-header',
        props.className?.wrapper,
      )}
    >
      {props.breadcrumb && (
        <Breadcrumbs
          className={{
            ...props.className,
            wrapper: cn(
              'mx-auto w-full max-w-screen-xl',
              'ea-homepage-header__breadcrumbs',
              templateSettings?.jobportal_header && 'bg-transparent',
              props.className?.wrapper,
            ),
          }}
          breadcrumb={props.breadcrumb}
          product={props.product}
        />
      )}
      <Container
        size="xl"
        className={cn(
          'flex flex-col rounded-none',
          'ea-homepage-header__container',
          {
            'items-center': variant === 'center',
            'items-start': variant === 'default',
          },
          props.className?.container,
        )}
      >
        {props.children}
        <div
          className={cn(
            'flex w-full flex-col gap-4',
            'ea-homepage-header__content',
            props.className?.content,
          )}
        >
          <div
            className={cn(
              'flex flex-row items-center  justify-between gap-4',
              'ea-homepage-header__heading-row',
              props.className?.heading_row,
            )}
          >
            {props.heading && (
              <HeadingTag
                {...(props.headingRole === 'primary' || !props.headingRole
                  ? { 'data-ea-primary-heading': true }
                  : {})}
                className={cn(
                  'text-[32px] font-bold',
                  'ea-homepage-header__heading',
                  props.className?.header,
                )}
              >
                {props.heading}
              </HeadingTag>
            )}
            <div className="flex gap-2">
              {showToggle && (
                <Button
                  variant="link"
                  className="hidden px-0 sm:flex"
                  onClick={() => setShowMore(prev => !prev)}
                >
                  {isExtended ? t('text528') : t('text529')}
                </Button>
              )}
              <div className="flex flex-row">
                <Button asChild variant="link" className="px-0">
                  {props.headerLink}
                </Button>
                <div className="flex flex-row items-center justify-end sm:hidden">
                  {props.mobileAction}
                </div>
              </div>
            </div>
          </div>
          {props.description && (
            <>
              <HtmlRendererRaw
                ref={descriptionRef}
                className={cn(
                  'h-auto min-h-min overflow-hidden text-sm text-muted-foreground transition-all duration-300 ease-in-out',
                  !isExtended ? 'line-clamp-6' : 'line-clamp-none',
                  'ea-homepage-header__description',
                  props.className?.description,
                )}
                html={props.description}
              />
              {showToggle && (
                <div className="flex justify-center">
                  <Button
                    variant="link"
                    className="flex px-0 sm:hidden"
                    onClick={() => setShowMore(prev => !prev)}
                  >
                    {isExtended ? t('text528') : t('text529')}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
}
